/**
 * Application shell.
 */
import { QueryClient, QueryClientProvider, notifyManager, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  AuthProvider,
  isRecoveryHashRoute,
  useAuth,
  clearAuthErrorHash,
} from '@platform/auth/authContext';
import { AuthGate } from '@features/auth/AuthScreens';
import { MarketingPage } from '@features/marketing/MarketingPage';
import { LegalPage, legalDocForHash } from '@features/marketing/LegalPages';
import { BusinessGate } from '@features/settings/BusinessGate';
import { AutoBackupScheduler } from '@features/settings/AutoBackupScheduler';
import { readActiveBusinessId } from '@data/services/businessSession';
import { healActiveBusinessPointer } from '@data/services/businessSession';
import { getDb, ensureDbReady } from '@data/db/database';
import { LoadingState } from '@ui/components/States';
import { Toaster } from '@ui/components/Toaster';
import { Router } from './router/Router';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: 0,
      refetchOnWindowFocus: false,
    },
  },
});

notifyManager.setBatchNotifyFunction((callback) => flushSync(callback));

function normalizeHashRoute(route: string): string {
  return route.split('?')[0] || '#/login';
}

function BusinessGatekeeper({ route }: { readonly route: string }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    void ensureDbReady().then(() => setReady(true));
  }, []);

  const active = useQuery({
    queryKey: ['businesses', 'active', 'gate'],
    queryFn: () => readActiveBusinessId(getDb()),
    staleTime: 0,
    gcTime: 0,
    enabled: ready,
  });

  useEffect(() => {
    if (!ready) return;
    void healActiveBusinessPointer(getDb());
  }, [ready]);

  if (!ready || active.isLoading) {
    return (
      <LoadingState label="Restoring your business…" className="min-h-screen justify-center" />
    );
  }

  // The business profile screen is the one place that can CREATE the first
  // business, so it must be reachable even when no business is selected yet.
  // Allow it through the gate, then let the profile screen decide create vs edit.
  const normalizedRoute = route.split('?')[0] ?? route;
  if (!active.data && !/^#\/settings\/business\/?$/.test(normalizedRoute)) {
    return <BusinessGate />;
  }

  return <Router />;
}

function AppContent() {
  const { state, justConfirmed } = useAuth();
  const [hash, setHash] = useState(() => window.location.hash);

  useEffect(() => {
    const handleHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const authRoutes = ['#/login', '#/signup', '#/forgot-password', '#/reset-password', '#/', '#'];
    const route = normalizeHashRoute(hash);
    const recoveryRoute = isRecoveryHashRoute(hash);
    const isLegalRoute = legalDocForHash(route) !== null;

    if (state === 'recovering' && route !== '#/reset-password') {
      window.location.hash = '#/reset-password';
      return;
    }

    if (
      state === 'unauthenticated' &&
      !authRoutes.includes(route) &&
      !recoveryRoute &&
      !isLegalRoute
    ) {
      window.location.hash = '#/';
      return;
    }

    if (state === 'authenticated') {
      clearAuthErrorHash();

      const onLoginPage = route === '#/login';
      if (authRoutes.includes(route) && !recoveryRoute && !(onLoginPage && justConfirmed)) {
        window.location.hash = '#/sales/invoices';
      }
    }
  }, [hash, state, justConfirmed]);

  const hashBase = hash.split('?')[0] ?? hash;
  const hashSection = hashBase.startsWith('#') ? hashBase.slice(1) : hashBase;
  const isMarketingRoute =
    hash === '#/' ||
    hash === '' ||
    hash === '#' ||
    ['#features', '#business', '#workflow', '#free'].includes(hashBase);
  const marketingSection: string | null = ['features', 'business', 'workflow', 'free'].includes(
    hashSection,
  )
    ? hashSection
    : null;
  const legalDoc = legalDocForHash(hashBase);

  useEffect(() => {
    if (state === 'unauthenticated' && isMarketingRoute && marketingSection) {
      requestAnimationFrame(() => {
        document
          .getElementById(marketingSection)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [isMarketingRoute, marketingSection, state]);

  if (legalDoc) {
    return <LegalPage doc={legalDoc} />;
  }

  if (state === 'loading' && !isRecoveryHashRoute(hash)) {
    return (
      <LoadingState label="Restoring your session..." className="min-h-screen justify-center" />
    );
  }

  if (state === 'recovering' || isRecoveryHashRoute(hash)) {
    return <AuthGate route={hash || '#/reset-password'} />;
  }

  if (state === 'unauthenticated') {
    if (isMarketingRoute) {
      return <MarketingPage />;
    }
    return <AuthGate route={hash || '#/login'} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="min-h-screen bg-slate-950">
        <BusinessGatekeeper route={hash} />
        {state === 'authenticated' ? <AutoBackupScheduler /> : null}
      </div>
      <Toaster />
    </QueryClientProvider>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
